import PageLayout from "pages/layout/PageLayout"
import { useEffect, useRef, useState } from "react"
import { AccessKeyTab, MENU_TAB_ACCESS_KEY } from "./address-book.type"
import { pushTo } from "helpers/history"
import { PATH } from "constants/path"
import { delay, searchParams } from "helpers/utils"
import { getProjectsMiddleware } from "pages/projects/services/api"
import { ProjectItemCard } from "./components/ProjectItemCard"
import { ProjectInviteeCard } from "./components/ProjectInviteeCard"
import { useBoolean, useNumber, useString, useWindowSize } from "helpers/hooks"
import {
  GetUsersInProjectProp,
  SetRoleInProjectProps,
} from "pages/project-component/types"
import { compact, isUndefined } from "lodash"
import { getListBuildMiddleware } from "pages/build-overview/builds.api"
import { ProjectBuildItemCard } from "./components/ProjectBuildItemCard"
import { ProjectBuildInviteeCard } from "./components/ProjectBuildInviteeCard"
import { SkeletonProjectCard } from "./skeleton/SkeletonAccessKeyProject"
import { SkeletonProjectBuildCard } from "./skeleton/SkeletonAccessKeyProjectBuild"
import InputDefault from "components/Input/InputDefault"
import { ParamsProjectsProps } from "pages/projects/types"
import ImageNoDataProject from "assets/images/icons/icon-no-data-project.svg"
import ImageNoDataBuild from "assets/images/icons/icon no-result.svg"

import Typography from "components/Typography"
import Button from "components/Button/Button"
import { configureStore } from "stores/configureStore"
import { closeModal, openModal } from "reducers/modal"
import IconLoading from "assets/images/loading-history.gif"
import { toast } from "react-toastify"
import LabelNotificationPage from "components/Notification/LabelNotificationPage"
import { EncryptionKeys } from "controllers/EncryptionController"
import {
  getUsersInProject,
  setRoleInProjectMiddleware,
} from "pages/project-component/services/api"
import { STATUS_RESPONSE } from "types"
import {
  getProjectBuildInviteeMiddleware,
  getProjectBuildMiddleware,
  postProjectBuildSendInviteeMiddleware,
} from "pages/project-build/api.services"
import {
  BUILD_INVITEE_STATUS,
  ProjectBuildInvitessDetail,
} from "pages/project-build/project-build.type"
import { syncNewVersionComponentInviteeMiddleWare } from "pages/project-component-detail/services"
import { useAddComponentInInvitee } from "hooks/useAddComponentInInvitee"
import { encryptionGCMHelper } from "helpers/encryption-gcm"

export const AccessKeyPage = () => {
  const [activeMenu, setActiveMenu] = useState("")
  const tabURL = searchParams("tab") || ""
  const projectIdURL = searchParams("projectId") || ""
  const projectBuildIdURL = searchParams("projectBuildId") || ""
  const [projects, setProjects] = useState<
    {
      id: string
      name: string
      code: string
      role_users: GetUsersInProjectProp[]
    }[]
  >([])
  const { isMobile } = useWindowSize()

  const [projectBuilds, setProjectBuilds] = useState<
    {
      id: string
      name: string
      project_id: string
      project_name: string
      project_code: string
      code: string
      components: string[]
      status: string
      users: GetUsersInProjectProp[]
    }[]
  >([])
  const projectIdActive = useString(projectIdURL)
  const projectBuildIdActive = useString(projectBuildIdURL)
  const isLoadingDataProject = useBoolean(false)
  const isLoadingDataProjectBuild = useBoolean(false)
  const inputSearch = useString("")
  const refBody = useRef<HTMLDivElement>(null)
  const isMoreData = useBoolean(false)
  const isLoadingScroll = useBoolean(false)
  const page = useNumber(1)
  const loadingShareAllKey = useBoolean(false)
  const PAGE_SIZE = 20
  const [currentPercent, setCurrentPercent] = useState(-1)
  const reloadPageProjectWhenShareAllKey = useBoolean(false)
  const { createNewComponentHistoryByOwner, syncNewVersionComponentInvitee } =
    useAddComponentInInvitee()

  useEffect(() => {
    if (tabURL && AccessKeyTab.includes(tabURL)) {
      setActiveMenu(tabURL)
    } else {
      setActiveMenu(AccessKeyTab[0])
      pushTo(PATH.accessKey, undefined, undefined, {
        tab: AccessKeyTab[0],
      })
    }
  }, [tabURL])

  useEffect(() => {
    if (tabURL !== MENU_TAB_ACCESS_KEY.PROJECT) {
      return
    }
    if (projectIdURL) {
      projectIdActive.setValue(projectIdURL)
    } else {
      projectIdActive.setValue("")
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectIdURL, tabURL])

  useEffect(() => {
    if (tabURL !== MENU_TAB_ACCESS_KEY.BUILD) {
      return
    }
    if (projectBuildIdURL) {
      projectBuildIdActive.setValue(projectBuildIdURL)
    } else {
      projectBuildIdActive.setValue("")
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectBuildIdURL, tabURL])

  const onChangeTab = (newMenu: string) => () => {
    if (newMenu === activeMenu) return
    setActiveMenu(newMenu)
    setProjectBuilds([])
    setProjects([])
    inputSearch.setValue("")
    isMoreData.setValue(false)
    page.setValue(1)
    pushTo(PATH.accessKey, undefined, undefined, {
      tab: newMenu,
    })
  }

  useEffect(() => {
    if (!activeMenu) {
      return
    }
    getMenuData(activeMenu)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMenu])

  const getMenuData = (menu: string, newSearch: string = "") => {
    // Call API to get data based on menu
    if (menu === MENU_TAB_ACCESS_KEY.PROJECT) {
      getProjectData(newSearch)
      return
    }
    getProjectBuildData(newSearch)
  }

  const onChangeInputSearch = (event) => {
    inputSearch.setValue(event.target.value)
  }

  const onKeyPress = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter") {
      event.preventDefault()
      pushTo(PATH.accessKey, undefined, undefined, {
        tab: activeMenu,
      })
      isMoreData.setValue(false)
      page.setValue(1)
      getMenuData(activeMenu, inputSearch.value)
    }
  }
  const handleScroll = async () => {
    const offsetHeight = refBody.current?.offsetHeight ?? 0
    const scrollHeight = refBody.current?.scrollHeight ?? 0
    const scrollTop = refBody.current?.scrollTop ?? 0

    if (scrollTop + offsetHeight <= scrollHeight - 10) return
    if (!isMoreData.value || isLoadingScroll.value) return

    isLoadingScroll.setValue(true)

    const nextPage = page.value + 1
    let dataRes: any[] = []

    try {
      if (activeMenu === MENU_TAB_ACCESS_KEY.PROJECT) {
        const projectParams: ParamsProjectsProps = {
          page: nextPage,
          pageSize: PAGE_SIZE,
          ...(inputSearch.value && { search: inputSearch.value }),
        }

        const newRes = await getProjectsMiddleware(
          projectParams,
          `api/project/get-list?loading=0`
        )
        dataRes = newRes.data
        const newProjects = [
          ...projects,
          ...newRes.data.map((project) => ({
            id: project.id,
            name: project.name,
            code: project.code,
            role_users:
              project.role_users?.filter(
                (el) => el.user_id !== project.created_by
              ) || [],
          })),
        ]

        setProjects(newProjects)
      } else {
        const buildParams: any = {
          page: nextPage,
          pageSize: PAGE_SIZE,
          filter: {
            is_archive: false,
            sorted_by: "latest_update",
            ...(inputSearch.value && { name: inputSearch.value }),
          },
        }

        dataRes = await getListBuildMiddleware(
          `my-builds?loading=0`,
          buildParams
        )

        const newBuilds = [
          ...projectBuilds,
          ...dataRes.map((build) => ({
            id: build.id,
            name: build.name,
            code: build.code,
            components: build.components,
            project_code: build.project_code,
            project_id: build.project_id,
            project_name: build.project_name,
            status: build.status,
            users: build.users || [],
          })),
        ]

        setProjectBuilds(newBuilds)
      }

      isMoreData.setValue(dataRes.length >= PAGE_SIZE)
      page.setValue(nextPage)
    } catch (error) {
    } finally {
      isLoadingScroll.setValue(false)
    }
  }

  const getProjectData = async (newSearch: string) => {
    try {
      isLoadingDataProject.setValue(true)
      const params: ParamsProjectsProps = {
        page: 1,
        pageSize: PAGE_SIZE,
      }
      if (newSearch) {
        params.search = newSearch
      }
      const dataRes = await getProjectsMiddleware(
        params,
        `api/project/get-list?loading=0`
      )
      isMoreData.setValue(dataRes.data.length >= PAGE_SIZE ? true : false)
      setProjects(
        dataRes.data.map((elProject) => {
          return {
            id: elProject.id,
            name: elProject.name,
            code: elProject.code,
            role_users:
              !isUndefined(elProject.role_users) && elProject.role_users.length
                ? elProject.role_users.filter(
                    (el) => el.user_id !== elProject.created_by
                  )
                : [],
          }
        })
      )
    } catch (error) {
      setProjects([])
    } finally {
      isLoadingDataProject.setValue(false)
    }
  }

  const getProjectBuildData = async (newSearch: string) => {
    const params: any = {
      page: 1,
      pageSize: PAGE_SIZE,
      filter: {
        is_archive: false,
        sorted_by: "latest_update",
      },
    }
    if (newSearch) {
      params.filter.name = newSearch
    }
    try {
      isLoadingDataProjectBuild.setValue(true)
      const dataRes = await getListBuildMiddleware(
        `my-builds?loading=0`,
        params
      )
      isMoreData.setValue(dataRes.length >= PAGE_SIZE ? true : false)

      setProjectBuilds(
        dataRes.map((elBuild) => {
          return {
            id: elBuild.id,
            name: elBuild.name,
            code: elBuild.code,
            components: elBuild.components,
            project_code: elBuild.project_code,
            project_id: elBuild.project_id,
            project_name: elBuild.project_name,
            status: elBuild.status,
            users: elBuild.users.length ? elBuild.users : [],
          }
        })
      )
    } catch (error) {
      setProjectBuilds([])
    } finally {
      isLoadingDataProjectBuild.setValue(false)
    }
  }

  const getMenuTab = () => {
    return (
      <div className="flex align gap-2 mb-[8px]">
        {AccessKeyTab.map((menu, index) => {
          return (
            <div className="relative" key={index}>
              <div
                className=" font-body font-medium cursor-pointer text-center rounded-4 flex items-center border-[#E4E4E4] border-[1px]"
                style={{
                  fontSize: 12,
                  height: "26px",
                  lineHeight: "18px",
                  color: activeMenu === menu ? "white" : "#111111",
                  background: activeMenu === menu ? "#111111" : "white",
                }}
                onClick={onChangeTab(menu)}
              >
                <p className="px-2">{menu}</p>
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  const renderListData = () => {
    if (activeMenu === MENU_TAB_ACCESS_KEY.PROJECT) {
      if (isLoadingDataProject.value) {
        return (
          <div className="flex flex-col gap-[12px] pr-[24px]">
            <SkeletonProjectCard />
          </div>
        )
      }
      return getListProject()
    }
    if (isLoadingDataProjectBuild.value) {
      return (
        <div className="flex flex-col gap-[12px] pr-[24px]">
          <SkeletonProjectBuildCard />
        </div>
      )
    }
    return getListProjectBuild()
  }

  const getListProject = () => {
    if (!projects.length) {
      return (
        <div className="h-full w-full flex flex-col items-center justify-center pr-[24px]">
          <img
            src={ImageNoDataProject}
            alt="icon"
            style={{
              height: 168,
            }}
          />
          <Typography
            textClass="font-base text-sm py-6"
            style={{
              color: "#111111",
              fontWeight: 600,
            }}
          >
            No projects found.
          </Typography>
        </div>
      )
    }
    return (
      <div className="flex flex-col gap-[12px] pr-[24px]">
        {projects.map((project, index) => {
          return (
            <ProjectItemCard
              key={index}
              project={project}
              projectIdActive={projectIdActive}
            />
          )
        })}
        {isMoreData.value ? <SkeletonProjectCard total={1} /> : null}
      </div>
    )
  }

  const getListProjectBuild = () => {
    if (!projectBuilds.length) {
      return (
        <div className="h-full w-full flex flex-col items-center justify-center pr-[24px]">
          <img
            src={ImageNoDataBuild}
            alt="icon"
            style={{
              height: 168,
            }}
          />
          <Typography
            textClass="font-base text-sm py-6"
            style={{
              color: "#111111",
              fontWeight: 600,
            }}
          >
            No build found.
          </Typography>
        </div>
      )
    }
    return (
      <div className="flex flex-col gap-[12px] pr-[24px]">
        {projectBuilds.map((build, index) => {
          return (
            <ProjectBuildItemCard
              key={index}
              build={build}
              projectBuildIdActive={projectBuildIdActive}
            />
          )
        })}
        {isMoreData.value ? <SkeletonProjectBuildCard total={1} /> : null}
      </div>
    )
  }

  const setRoleInProjectAsync = (
    projectId: string,
    data: SetRoleInProjectProps,
    silent: boolean
  ): Promise<{ success: boolean; message: string }> => {
    return new Promise((resolve) => {
      setRoleInProjectMiddleware(
        projectId,
        data,
        silent,
        (type, messenger) => {
          resolve({
            success: type === STATUS_RESPONSE.SUCCESS,
            message: messenger,
          })
        },
        false,
        true
      )
    })
  }

  const handleShareKey =
    (
      projectId: string,
      projectBuildId: string,
      emails: string[],
      originInvite: ProjectBuildInvitessDetail,
      reloadData = true,
      isSentMailAll = false
    ) =>
    async () => {
      if (!emails.length) {
        return
      }
      const generateDataKeyPairUsers = await syncNewVersionComponentInvitee(
        originInvite.project_build_components,
        projectId,
        originInvite,
        projectBuildId,
        originInvite.conversation_id,
        emails
      )
      return new Promise<void>((resolve) => {
        setTimeout(() => {
          postProjectBuildSendInviteeMiddleware(
            originInvite.id,
            "",
            async (type: STATUS_RESPONSE) => {
              if (type === STATUS_RESPONSE.SUCCESS) {
              } else {
              }
              resolve()
            },
            emails,
            generateDataKeyPairUsers,
            isSentMailAll,
            isSentMailAll
          )
        }, 500)
      })
    }

  const handleShareAllKeyProjectAndBuild = () => {
    configureStore.dispatch(
      openModal({
        type: "Delete",
        props: {
          deleteModal: {
            title: (
              <div className="flex flex-col gap-4 text-gray-900">
                <p className="text-base font-semibold">
                  Are you sure you want to share all keys?
                </p>

                <p className="text-sm">
                  This action will share all relevant keys associated with the
                  Project or Conversation with users who have been granted
                  access. The keys include:
                </p>

                <ul className="flex flex-col gap-2 pl-4 text-sm list-none">
                  {[
                    {
                      label: "Project Key",
                      desc: "Access to the entire Project.",
                    },
                    {
                      label: "Conversation Key",
                      desc: "Access to view and message in the conversation.",
                    },
                    {
                      label: "Build Key",
                      desc: "Access to view additional build information.",
                    },
                    {
                      label: "Component Key",
                      desc: "Access to view component information in the conversation.",
                    },
                  ].map((item) => (
                    <li key={item.label} className="flex items-start">
                      <span className="mt-2 h-1 w-1 rounded-full bg-gray-900 mr-2" />
                      <div>
                        <span className="font-semibold whitespace-nowrap">
                          {item.label}
                        </span>
                        <span> — {item.desc}</span>
                      </div>
                    </li>
                  ))}
                </ul>

                <div className="flex flex-col gap-1 text-sm">
                  <p>
                    Recipients will be able to access and utilize the
                    corresponding resources.
                  </p>
                  <p>
                    Please confirm that you understand the implications of
                    sharing these keys.
                  </p>
                </div>
              </div>
            ),
            label: `Warning`,
            content: "",
            titleButton: `Confirm`,
            colorYellowButton: true,
            styleContent: {
              padding: 0,
            },
            contentWrapperStyles: {
              padding: "0px 0px 49px 0px",
              alignItems: "start",
            },
            styleRoot: {
              width: 545,
            },
            onSubmit: async () => {
              loadingShareAllKey.setValue(true)
              setCurrentPercent(-1)
              closeModal()

              const dataProjects = await getProjectsMiddleware(
                {
                  page: 1,
                  pageSize: 99999,
                },
                `api/project/get-list?loading=0`
              )
              const dataProjectBuilds = await getListBuildMiddleware(
                `my-builds?loading=0`,
                {
                  page: 1,
                  pageSize: 99999,
                  filter: {
                    is_archive: false,
                    sorted_by: "latest_update",
                  },
                }
              )
              const totalData =
                dataProjects.data.length + dataProjectBuilds.length

              if (!totalData) {
                toast(
                  <LabelNotificationPage
                    messenger={`No keys are available to share at this time!`}
                    type="warning"
                  />
                )
                loadingShareAllKey.setValue(false)
                return
              }
              setCurrentPercent(0)
              const projectEncryptionKeys = JSON.parse(
                localStorage.getItem(EncryptionKeys.projectEncryptionKeys) ||
                  "{}"
              )
              if (dataProjects.data.length) {
                for (let index = 0; index < dataProjects.data.length; index++) {
                  const elProject = dataProjects[index]
                  const projectKey = projectEncryptionKeys[elProject.id]

                  if (projectKey) {
                    const dataInviteeRes = await getUsersInProject(
                      elProject.id,
                      true
                    )
                    const inviteeNotHasKey = dataInviteeRes.filter(
                      (el) => !el.has_key
                    )
                    if (inviteeNotHasKey.length) {
                      for (const elInviteeRequest of inviteeNotHasKey) {
                        await setRoleInProjectAsync(
                          elProject.id,
                          {
                            email: elInviteeRequest.email,
                            role: elInviteeRequest.role,
                          },
                          false
                        )
                      }
                    }
                  } else {
                    toast(
                      <LabelNotificationPage
                        messenger={`${elProject.code} - ${elProject.name}: No keys are available to share at this time!`}
                        type="error"
                      />
                    )
                  }
                  const percent = Math.ceil(((index + 1) / totalData) * 100)
                  setCurrentPercent(Math.min(percent, 99))
                  await delay(1000)
                }
                if (
                  activeMenu === MENU_TAB_ACCESS_KEY.PROJECT &&
                  projectIdActive.value
                ) {
                  reloadPageProjectWhenShareAllKey.setValue(true)
                }
              }

              if (dataProjectBuilds.length) {
                for (let index = 0; index < dataProjectBuilds.length; index++) {
                  const elBuild = dataProjectBuilds[index]
                  const projectKey = projectEncryptionKeys[elBuild.project_id]
                  if (projectKey) {
                    const dataProjectBuildRes = await getProjectBuildMiddleware(
                      elBuild.id,
                      undefined,
                      true
                    )
                    if (
                      dataProjectBuildRes?.id &&
                      dataProjectBuildRes?.buildKey?.encryptedKey
                    ) {
                      const decryptedBuildKey =
                        await encryptionGCMHelper.decryptContext(
                          projectKey,
                          dataProjectBuildRes?.buildKey?.encryptedKey
                        )
                      const isBuildKeyValid =
                        decryptedBuildKey &&
                        typeof decryptedBuildKey === "string" &&
                        decryptedBuildKey.length === 64 &&
                        decryptedBuildKey !==
                          dataProjectBuildRes?.buildKey?.encryptedKey
                      if (isBuildKeyValid) {
                        const conversationKeys =
                          dataProjectBuildRes.conversationKeys ?? []
                        const notDecryptedConversationKeys = await Promise.all(
                          conversationKeys.map(
                            async ({ encryptedKey, conversationCode }) => {
                              if (!encryptedKey) {
                                return { conversationCode, isDecrypted: false }
                              }

                              const decrypted =
                                await encryptionGCMHelper.decryptContext(
                                  projectKey,
                                  encryptedKey
                                )
                              const isDecrypted =
                                typeof decrypted === "string" &&
                                decrypted.length === 64 &&
                                decrypted !== encryptedKey

                              return { conversationCode, isDecrypted }
                            }
                          )
                        )
                        const notDecryptedConversationKeysNotDecrypted =
                          notDecryptedConversationKeys.filter(
                            ({ isDecrypted }) => !isDecrypted
                          )

                        if (
                          notDecryptedConversationKeysNotDecrypted.length > 0
                        ) {
                        } else {
                          let dataInvitees =
                            await getProjectBuildInviteeMiddleware(
                              dataProjectBuildRes.id,
                              true
                            )
                          const components: {
                            project_component_history_id: string
                            code: string
                            version: string
                            type: number
                            conversation_id: string
                          }[] = []
                          dataInvitees.forEach((invitee) => {
                            if (
                              invitee.project_build_components.length &&
                              invitee.status === BUILD_INVITEE_STATUS.ACTIVATE
                            ) {
                              invitee.project_build_components.forEach(
                                (elComponent) => {
                                  if (
                                    !elComponent.componentShareKey ||
                                    !elComponent.project_component_history_share_id ||
                                    elComponent.project_component_history_id !==
                                      elComponent.project_component_history_latest_id
                                  ) {
                                    const historyId =
                                      elComponent.project_component_history_latest_id ||
                                      elComponent.project_component_history_id
                                    const componentExisted = components.find(
                                      (el) =>
                                        el.project_component_history_id ===
                                        historyId
                                    )
                                    if (!componentExisted) {
                                      components.push({
                                        project_component_history_id: historyId,
                                        code: elComponent.code,
                                        version: elComponent.version,
                                        type: elComponent.type,
                                        conversation_id:
                                          invitee.conversation_id,
                                      })
                                    }
                                  }
                                }
                              )
                            }
                          })
                          if (components.length) {
                            for (const elComponent of components) {
                              const { dataComponentRequest } =
                                await createNewComponentHistoryByOwner(
                                  dataProjectBuildRes.project_id,
                                  elComponent.type,
                                  elComponent.project_component_history_id,
                                  elComponent.conversation_id
                                )

                              await syncNewVersionComponentInviteeMiddleWare(
                                dataComponentRequest,
                                [],
                                elComponent.project_component_history_id,
                                dataProjectBuildRes.id,
                                []
                              )
                            }
                            dataInvitees =
                              await getProjectBuildInviteeMiddleware(
                                dataProjectBuildRes.id
                              )
                          }

                          for (const elNewInvitee of dataInvitees) {
                            if (
                              elNewInvitee.status ===
                              BUILD_INVITEE_STATUS.DEACTIVATE
                            ) {
                              continue
                            }

                            const emails: string[] = []
                            if (
                              elNewInvitee.has_key_invitee &&
                              elNewInvitee.email &&
                              (!elNewInvitee.has_key_invitee.build ||
                                !elNewInvitee.has_key_invitee?.component ||
                                !elNewInvitee.has_key_invitee?.conversation)
                            ) {
                              emails.push(elNewInvitee.email)
                            }
                            if (
                              !isUndefined(elNewInvitee.publicKeyCCs) &&
                              elNewInvitee.publicKeyCCs.length
                            ) {
                              elNewInvitee.publicKeyCCs.forEach((elCC) => {
                                if (
                                  elCC.has_key &&
                                  elCC.email &&
                                  (!elCC.has_key.build ||
                                    !elCC.has_key?.component ||
                                    !elCC.has_key?.conversation)
                                ) {
                                  emails.push(elCC.email)
                                }
                              })
                            }
                            if (emails.length) {
                              const shareKeyFn = handleShareKey(
                                dataProjectBuildRes.project_id,
                                dataProjectBuildRes.id,
                                compact(emails),
                                elNewInvitee,
                                false,
                                false
                              )
                              await shareKeyFn()
                            } else {
                            }
                          }
                        }
                      } else {
                        toast(
                          <LabelNotificationPage
                            messenger={`Error`}
                            type="error"
                          />
                        )
                        break
                      }
                    } else {
                    }
                  } else {
                  }
                  const percentBuild = Math.ceil(
                    ((dataProjects.data.length + index + 1) / totalData) * 100
                  )
                  setCurrentPercent(Math.min(percentBuild, 99))
                  await delay(1000)
                }
              }

              setCurrentPercent(100)
              toast(
                <LabelNotificationPage
                  messenger={`Share all keys successfully!`}
                  type="success"
                />
              )
              setTimeout(() => {
                loadingShareAllKey.setValue(false)
                setCurrentPercent(-1)
              }, 2000)
            },
          },
        },
      })
    )
  }

  const renderBody = () => {
    if (activeMenu === MENU_TAB_ACCESS_KEY.PROJECT) {
      return (
        <ProjectInviteeCard
          projectIdActive={projectIdActive}
          reloadPageProjectWhenShareAllKey={reloadPageProjectWhenShareAllKey}
        />
      )
    }
    return (
      <ProjectBuildInviteeCard projectBuildIdActive={projectBuildIdActive} />
    )
  }

  if (isMobile) {
    pushTo(PATH.projects)
    return null
  }
  return (
    <PageLayout heightHeader={0}>
      <div className="h-full md:flex">
        <div
          className={`w-full flex transition-all duration-500 min-w-[342px] md:w-[342px]
            border-r border-[#E4E4E4]`}
        >
          <div className={`flex flex-col flex-1 pl-6 pb-6`}>
            <div className="flex items-center justify-between pr-[24px]">
              <p className="font-semibold text-black text-base min-h-[48px] items-center flex">
                Access Keys
              </p>
              <Button
                title="Share all keys"
                sizeBtn="small"
                colorBtn="yellow"
                onClick={handleShareAllKeyProjectAndBuild}
                isDisabledBtn
                heightBtn={35}
                widthBtn={170}
              />
            </div>

            {getMenuTab()}
            <div className="flex items-center justify-between mb-[12px] pr-[24px]">
              <InputDefault
                onChange={onChangeInputSearch}
                placeholder="Enter search"
                onKeyPress={onKeyPress}
                value={inputSearch.value}
                style={{
                  height: 30,
                  flexGrow: 1,
                }}
              />
            </div>

            <div
              className="flex flex-col flex-1 overflow-auto"
              ref={refBody}
              onScroll={handleScroll}
            >
              {renderListData()}
            </div>
          </div>
        </div>

        <div className="flex w-full bg-[#FFF]">{renderBody()}</div>
      </div>
      {loadingShareAllKey.value ? (
        <div
          className="inset-0 flex fixed items-center justify-center"
          style={{
            zIndex: 10000,
            backgroundColor: "#22222285",
          }}
        >
          <div
            className="p-3 bg-white rounded-md flex flex-col"
            style={{
              width: 400,
            }}
          >
            <div className="flex flex-col relative">
              <div
                className="flex justify-center"
                style={{
                  height: 216,
                }}
              >
                <img
                  src={IconLoading}
                  alt="icon"
                  style={{
                    width: 216,
                  }}
                />
              </div>
              <div className="absolute left-0 bottom-0 right-0">
                <div className="w-full h-[30px] flex justify-center items-end">
                  {currentPercent > -1 ? (
                    <div className="bg-[#56a01226] border border-[#56a01226] rounded-[8px] w-full h-full">
                      <div
                        className="max-w-full h-full rounded-[8px] bg-[#56A012] flex items-center justify-center"
                        style={{
                          width: `${currentPercent}%`,
                        }}
                      >
                        <p className="font-semibold text-[13px] text-[#FFF]">
                          {`${currentPercent}%`}
                        </p>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </PageLayout>
  )
}
